import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@workspace/ui/components/Button'
import { Input, PasswordInput } from '@workspace/ui/components/Textfield'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@workspace/ui/components/Form'
import { Card } from '@workspace/ui/components/Card'
import { Separator } from '@workspace/ui/components/Separator'
import { api } from '@/shared/lib/api'
import { login as loginToStore } from '@/shared/store/auth.store'
import { initializeTokenService } from '@/shared/lib/tokenService'
import { getEnv } from '@/shared/lib/env'

export const Route = createFileRoute('/login')({
    component: LoginPage,
    validateSearch: z.object({
        redirect: z.string().optional(),
        blocked: z
            .union([z.string(), z.boolean()])
            .optional()
            .transform(val => val === 'true' || val === true),
        user: z.string().optional(),
    }),
})

const createLoginSchema = (t: (key: string) => string) =>
    z.object({
        email: z.string().trim().min(1, t('login.validation.emailRequired')).email(t('login.validation.emailInvalid')),
        password: z.string().min(1, t('login.validation.passwordRequired')).min(6, t('login.validation.passwordMin')),
    })

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>

type LoginErrorData = {
    code?: string
    message?: string
    lockoutMinutes?: number
    maxAttempts?: number
    failedAttempts?: number
}

function getLoginErrorData(error: unknown): LoginErrorData | undefined {
    if (error === null || typeof error !== 'object' || !('response' in error)) return undefined
    const res = (error as { response?: { data?: unknown } }).response
    if (res?.data == null || typeof res.data !== 'object') return undefined
    return res.data as LoginErrorData
}

function LoginPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const search = useSearch({ from: '/login' })
    const [isLoading, setIsLoading] = useState(false)
    const [blockedAccount, setBlockedAccount] = useState(Boolean(search.blocked || search.user === 'Blocked'))
    const [inactiveAccount, setInactiveAccount] = useState(Boolean(search.user === 'Inactive'))
    const [rateLimitInfo, setRateLimitInfo] = useState<{ minutes: number; maxAttempts: number } | null>(null)

    const loginSchema = useMemo(() => createLoginSchema(t), [t])

    const form = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    })

    // Update form validation when language changes
    useEffect(() => {
        form.clearErrors()
    }, [loginSchema, form.clearErrors])

    useEffect(() => {
        setBlockedAccount(Boolean(search.blocked || search.user === 'Blocked'))
        setInactiveAccount(Boolean(search.user === 'Inactive'))
    }, [search.blocked, search.user])

    const onSubmit = async (data: LoginFormData) => {
        setIsLoading(true)

        try {
            // Call the auth login API
            const response = await api.auth.login({
                email: data.email.trim(),
                password: data.password,
            })

            // Store user, access token, refresh token, and expiration in auth store
            loginToStore(
                response.user,
                response.token.accessToken,
                response.refreshToken.refreshToken,
                response.token.expiresIn,
            )

            // Initialize token service to start proactive refresh
            initializeTokenService()

            // Navigate to the redirect URL or dashboardff
            const redirectTo = search.redirect || '/dashboard'
            navigate({ to: redirectTo })
        } catch (error: unknown) {
            const errorData = getLoginErrorData(error)
            const errorCode = errorData?.code ?? ''
            const message = errorData?.message ?? ''

            const isBlocked =
                message === 'error.userBlocked' ||
                message.includes('userBlocked') ||
                message.includes('blocked') ||
                message.includes('BLOCKED') ||
                message.includes('tạm khóa') ||
                errorCode === 'USER_BLOCKED' ||
                errorCode === 'BLOCKED'

            if (errorCode === 'USER_TEMPORARILY_BLOCKED') {
                setRateLimitInfo({
                    minutes: errorData?.lockoutMinutes ?? 0,
                    maxAttempts: errorData?.maxAttempts ?? 0,
                })
            } else if (isBlocked) {
                setBlockedAccount(true)
            } else if (errorCode === 'INVALID_CREDENTIALS' && errorData?.failedAttempts !== undefined) {
                const { failedAttempts, maxAttempts: maxAtt } = errorData
                form.setError('root', {
                    type: 'manual',
                    message: t('login.validation.loginFailedWithAttempts', { failedAttempts, maxAttempts: maxAtt }),
                })
            } else {
                form.setError('root', {
                    type: 'manual',
                    message: t('login.validation.loginFailed'),
                })
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleAzureLogin = () => {
        // Build Azure authorize URL
        const tenantId = getEnv('VITE_AZURE_TENANT_ID')
        const clientId = getEnv('VITE_AZURE_CLIENT_ID')

        // Auto-detect redirect URI based on current origin (localhost or production)
        const currentOrigin = window.location.origin
        const redirectUri = getEnv('VITE_AZURE_REDIRECT_URI') || `${currentOrigin}/auth/callback`

        const scopes = getEnv('VITE_AZURE_SCOPES') || 'openid profile email'

        // Redirect to Azure login
        window.location.href =
            `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
            `client_id=${clientId}&` +
            `response_type=code&` +
            `redirect_uri=${encodeURIComponent(redirectUri)}&` +
            `response_mode=query&` +
            `scope=${encodeURIComponent(scopes)}`
    }

    return (
        <div className="flex min-h-screen items-center justify-center flex-col p-4">
            <img
                src="/auth-bg.png"
                alt="Background"
                className="absolute inset-0 w-full h-full object-cover opacity-[8%] -z-1"
            />

            <img src="/logo.png" alt="Admin Panel Logo" className="w-20 h-20 mb-4" />

            {inactiveAccount ? (
                <Card className="w-full max-w-[400px] p-8 shadow-md bg-background">
                    <div className="text-left space-y-4">
                        <h2 className="text-xl font-bold text-center">{t('login.noPermissionTitle')}</h2>
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">{t('login.noPermissionMessage1')}</p>
                            <p className="text-sm text-muted-foreground">{t('login.noPermissionMessage2')}</p>
                        </div>
                        <Button variant="outline" className="w-full mt-6" onPress={() => setInactiveAccount(false)}>
                            {t('login.backButton')}
                        </Button>
                    </div>
                </Card>
            ) : rateLimitInfo !== null ? (
                <Card className="w-full max-w-[400px] p-8 shadow-md bg-background">
                    <div className="text-left space-y-4">
                        <h2 className="text-xl font-bold text-center">{t('login.rateLimitedTitle')}</h2>
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground text-center">
                                {t('login.rateLimitedMessage', {
                                    lockoutMinutes: rateLimitInfo.minutes,
                                    maxAttempts: rateLimitInfo.maxAttempts,
                                })}
                            </p>
                        </div>
                        <Button variant="outline" className="w-full mt-6" onPress={() => setRateLimitInfo(null)}>
                            {t('login.backButton')}
                        </Button>
                    </div>
                </Card>
            ) : blockedAccount ? (
                <Card className="w-full max-w-[400px] p-8 shadow-md bg-background">
                    <div className="text-left space-y-4">
                        <h2 className="text-xl font-bold text-center">
                            {t('login.accountLockedTitle', 'Tài khoản tạm khóa')}
                        </h2>
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                                {t(
                                    'login.accountLockedMessage',
                                    'Tài khoản của bạn đã bị khóa, không thể truy cập được vào hệ thống.',
                                )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {t(
                                    'login.accountLockedSupport',
                                    'Vui lòng liên hệ Quản trị viên hoặc bộ phận liên quan để được hỗ trợ.',
                                )}
                            </p>
                        </div>
                        <Button variant="outline" className="w-full mt-6" onPress={() => setBlockedAccount(false)}>
                            {t('login.backButton', 'Quay lại')}
                        </Button>
                    </div>
                </Card>
            ) : (
                <Card className="w-full max-w-[360px] p-6 shadow-md bg-background gap-4">
                    <div className="">
                        <h1 className="text-[20px] font-bold text-center mb-4">{t('login.title')}</h1>
                        <p className="text-center text-sm font-medium text-muted-foreground">{t('login.subtitle')}</p>
                    </div>

                    <Button
                        variant="outline"
                        className="w-full gap-2 rounded-lg border-border-medium bg-background"
                        onPress={handleAzureLogin}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0)">
                                <rect x="10.63" y="10.63" width="6.25" height="6.25" fill="#FEBA08" />
                                <rect x="3.13" y="10.63" width="6.25" height="6.25" fill="#05A6F0" />
                                <rect x="10.63" y="3.13" width="6.25" height="6.25" fill="#80BC06" />
                                <rect x="3.13" y="3.13" width="6.25" height="6.25" fill="#F25325" />
                            </g>
                            <defs>
                                <clipPath id="clip0">
                                    <rect width="13.75" height="13.75" fill="white" transform="translate(3.13 3.13)" />
                                </clipPath>
                            </defs>
                        </svg>
                        <span className="text-sm font-medium">{t('login.azureButton')}</span>
                    </Button>

                    <div className="flex items-center gap-2">
                        <Separator className="flex-1" />
                        <span className="text-sm text-muted-foreground">{t('login.or')}</span>
                        <Separator className="flex-1" />
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-medium">{t('login.email')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t('login.emailPlaceholder')}
                                                autoComplete="email"
                                                className="rounded-lg border-border"
                                                {...field}
                                                onChange={e => field.onChange(e.target.value.trim())}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-medium">{t('login.password')}</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                placeholder={t('login.passwordPlaceholder')}
                                                className="rounded-lg border-border"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {form.formState.errors.root && (
                                <p className="text-sm font-medium text-red-600">{form.formState.errors.root.message}</p>
                            )}

                            <Button
                                variant="default"
                                type="submit"
                                className="w-full rounded-lg"
                                isDisabled={isLoading}
                            >
                                {isLoading ? t('login.loggingIn') : t('login.loginButton')}
                            </Button>
                        </form>
                    </Form>
                </Card>
            )}
        </div>
    )
}
